import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { state, ui } from '../../interfaces/interfaces';
import { useAppSelector } from "@store/hooks";
import { companiesHouseGetCompanyDetails } from '../../utilities/apis/wbs/endpoints';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

const CompaniesHouseRecordDialogTitle = (props: DialogTitleProps) => {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
};

const handleClose = () => {
    ui.CompaniesHouseRecordDialog = false
};
const handleAccept = (companyProfile: CompaniesHouseOutput) => {
    state.BookingPaymentMethodCompanyRegistrationAccepted = true
    state.JobCompanyRegistrationName = companyProfile.company_name || ""
    state.JobCompanyRegistrationNumber = companyProfile.company_number || ""
    ui.CompaniesHouseRecordDialog = false
};
const handleReject = () => {
    state.BookingPaymentMethodCompanyRegistrationAccepted = false
    state.JobCompanyRegistrationName = undefined
    state.JobCompanyRegistrationNumber = undefined
    ui.CompaniesHouseRecordDialog = false
};
const dateStrRegex = new RegExp(/^\d\d\d\d-\d\d-\d\d$/);
const regexDateValidator = (dateStr: string) => dateStrRegex.test(dateStr)

const companyCheckedAccepted = (companyProfile: CompaniesHouseOutput | undefined) => ((companyProfile && regexDateValidator(String(companyProfile?.date_of_creation)) && !companyProfile?.has_been_liquidated && companyProfile.company_status?.toLowerCase() === "active")
    // && regexDateValidator(String(companyProfile?.accounts?.last_accounts?.made_up_to))
) || false

interface CompaniesHouseOutput extends Omit<Awaited<ReturnType<typeof companiesHouseGetCompanyDetails>>, "sic_codes"> {
    sic_codes: Array<{
        sic_code: string,
        description: string
    }>
}

type CompaniesHouseRecordDialogProps = {
    companyProfile: CompaniesHouseOutput | null
}

const CompaniesHouseRecordDialog: React.FC<CompaniesHouseRecordDialogProps> = ({ companyProfile }) => {
    const companiesHouseRecordDialog = useAppSelector(({ ui }) => ui.CompaniesHouseRecordDialog)

    return (
        <StyledDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={companiesHouseRecordDialog}
        >
            <CompaniesHouseRecordDialogTitle id="customized-dialog-title" onClose={handleClose}>
                Companies House Record
            </CompaniesHouseRecordDialogTitle>
            {companyProfile && companyProfile.company_number && <DialogContent dividers>
                <Typography gutterBottom><b>Company number: </b>{`${companyProfile.company_number}`}</Typography>
                <Typography gutterBottom><b>Company name: </b>{`${companyProfile.company_name}`}</Typography>
                <Typography gutterBottom><b>Office addrr: </b>{`${companyProfile.registered_office_address?.address_line_1 || ""}, ${(companyProfile.registered_office_address?.address_line_2 && ",") || ""} ${companyProfile.registered_office_address?.locality || ""}, ${companyProfile.registered_office_address?.postal_code}`}</Typography>
                <Typography gutterBottom><b>Date of creation: </b>{`${companyProfile.date_of_creation}`}</Typography>
                <Typography gutterBottom><b>Date of Last Accounts: </b>{`${companyProfile.accounts?.last_accounts?.made_up_to || "NO DATA"}`}</Typography>
                <Typography gutterBottom><b>Company status: </b>{`${companyProfile.company_status?.toUpperCase()}`}</Typography>
                {companyProfile?.date_of_cessation && <Typography gutterBottom><b>Dissolved on: </b>{`${companyProfile.date_of_cessation}`}</Typography>}
                <Typography gutterBottom><b>Has been liquidated: </b>{`${companyProfile.has_been_liquidated ? "Yes" : "No"}`}</Typography>
                <Typography gutterBottom><b>Has insolvency history: </b>{`${companyProfile.has_insolvency_history ? "Yes" : "No"}`}</Typography>
                <Typography gutterBottom><b>Jurisdiction: </b>{`${companyProfile.jurisdiction}`}</Typography>
                <Typography gutterBottom><b>Type: </b>{`${companyProfile.type?.toUpperCase()}`}</Typography>
                {companyProfile?.sic_codes && <Typography component="div" gutterBottom><b>Standard Industrial Classification: </b>{companyProfile.sic_codes.map(({ sic_code, description }) => (
                    <Typography key={sic_code} gutterBottom>{`(${sic_code}) ${description}`}</Typography>
                ))}</Typography>}

            </DialogContent>}
            <DialogActions>
                {companyProfile && companyCheckedAccepted(companyProfile) && <Button color='success' variant='contained' autoFocus onClick={() => handleAccept(companyProfile)}>
                    Accept
                </Button>}
                <Button color='error' variant='outlined' autoFocus onClick={handleReject}>
                    Reject
                </Button>
            </DialogActions>
        </StyledDialog >
    );
}

export default React.memo(CompaniesHouseRecordDialog)